import { useAntNotification } from 'components/ant/AntNotification';
import { useCallback, useState } from 'react';
import { routesApi } from 'routes/api';
import api from 'utils/axios';
import { wait } from 'utils/promiseUtils/wait';

export type JobStatus = 'queued' | 'working' | 'retrying' | 'complete' | 'failed' | 'interrupted';

export type JobTerminalStatus = 'complete' | 'failed';

export interface IAsyncJobStatusResponse<T extends string> {
  status: JobStatus | null;
  values: Record<T, string>;
}

export interface IAsyncJobStatusProps<T extends string> {
  errorMessage?: string;
  keys?: string[];
  onCompleted: (status: JobTerminalStatus, values: Record<T, string>) => void;
  onProgress?: (status: JobStatus | null, values: Record<T, string>) => void;
}

export type AsyncJobStatusHookReturnType = [(jobId: string) => void, boolean];

const POLL_INTERVAL_MS = 1000;
const DEFAULT_FETCH_ERROR = 'Failed to fetch async job status. Please try again.';

const isFailedOutcome = (status: JobStatus | null): boolean => {
  return status === 'failed' || status === 'interrupted' || status == null;
};

const isInProgress = (status: JobStatus | null): boolean => {
  return status === 'queued' || status === 'working' || status === 'retrying';
};

const useAsyncJobStatus = <T extends string>({
  errorMessage,
  keys,
  onCompleted,
  onProgress,
}: IAsyncJobStatusProps<T>): AsyncJobStatusHookReturnType => {
  const notification = useAntNotification();
  const [loading, setLoading] = useState(false);

  const startPolling = useCallback(
    (jobId: string) => {
      setLoading(true);

      const notifyFetchError = () => {
        setLoading(false);
        notification.error({ message: errorMessage || DEFAULT_FETCH_ERROR });
      };

      const params = Array.isArray(keys) ? { keys } : {};

      async function pollJobStatus(): Promise<void> {
        let stop = false;

        try {
          while (!stop) {
            await wait(POLL_INTERVAL_MS);

            const { data } = await api.get<IAsyncJobStatusResponse<T>>(routesApi.asyncJobPath(jobId), {
              params,
            });

            const { status, values } = data;

            if (status === 'complete') {
              setLoading(false);
              onCompleted('complete', values);

              stop = true;
            } else if (isFailedOutcome(status)) {
              setLoading(false);
              onCompleted('failed', values);

              stop = true;
            } else if (isInProgress(status)) {
              onProgress?.(status, values);
            } else {
              setLoading(false);
              onCompleted('failed', values);
              notification.error({ message: errorMessage || DEFAULT_FETCH_ERROR });

              stop = true;
            }
          }
        } catch {
          notifyFetchError();
        }
      }

      void pollJobStatus();
    },
    [errorMessage, keys, onCompleted, onProgress, notification],
  );

  return [startPolling, loading];
};

export default useAsyncJobStatus;
